import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faArrowDownLong } from "@fortawesome/free-solid-svg-icons";
import CommonModal from "../CommonModal";
import Button from "../../Elements/Button";
import { networkService } from "../../config/axios";

const keptItems = (replacement) => {
  const items = ["Overlays, hotspots, links and lightboxes placed on the pages"];
  const c = replacement.comparison;
  const navigatorTouched =
    c?.navigator_drafted ||
    c?.heavily_edited_question_count > 0 ||
    c?.orphaned_question_count > 0;

  if (!navigatorTouched) {
    items.push("Navigator questions, CTAs and weighting");
  }

  return items;
};

const pageList = (pages) => {
  const arr = (pages || []).filter((p) => p != null);
  if (arr.length === 0) return "";
  if (arr.length === 1) return `Page ${arr[0]}`;
  return `Pages ${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
};

const buildComparisonLines = (c) => {
  if (!c || c.status !== "done") return [];

  const lines = [];

  if (c.migrated === false) {
    lines.push(
      "We couldn't confidently match how pages moved between the old and new PDF, so page numbers were left as they were — anything below reflects pages that no longer exist, not a guessed reorder."
    );
  }

  if (c.removed_pages?.length) {
    lines.push(`${pageList(c.removed_pages)} from the old PDF ${c.removed_pages.length === 1 ? "is" : "are"} gone`);
  }
  (c.added_pages || []).forEach((p) => {
    lines.push(p.summary ? `New on page ${p.new_page}: ${p.summary}` : `Page ${p.new_page} is new content`);
  });
  (c.reordered || []).forEach((r) => {
    lines.push(`Page ${r.from} is now page ${r.to}`);
  });
  if (c.edited_pages?.length) {
    lines.push(`${pageList(c.edited_pages)} ${c.edited_pages.length === 1 ? "was" : "were"} revised`);
  }
  if (c.moved_element_count > 0) {
    lines.push(
      `${c.moved_element_count} element${c.moved_element_count === 1 ? "" : "s"} ` +
        `moved to follow ${c.moved_element_count === 1 ? "its" : "their"} page` +
        (c.moved_to_pages?.length ? ` (now on ${pageList(c.moved_to_pages).toLowerCase()}) — review placement` : " — review placement")
    );
  }
  if (c.moved_question_count > 0) {
    lines.push(
      `${c.moved_question_count} Navigator question${c.moved_question_count === 1 ? "" : "s"} ` +
        `now on ${c.moved_question_pages?.length ? pageList(c.moved_question_pages).toLowerCase() : "a different page"}`
    );
  }
  if (c.relinked_count > 0) {
    lines.push(
      `${c.relinked_count} Navigator "go to page" link${c.relinked_count === 1 ? "" : "s"} ` +
        `updated to follow ${c.relinked_count === 1 ? "its" : "their"} page`
    );
  }
  if (c.relinked_to_removed_pages?.length) {
    lines.push(
      `Some Navigator links pointed to ${pageList(c.relinked_to_removed_pages).toLowerCase()}, which no longer ` +
        `${c.relinked_to_removed_pages.length === 1 ? "exists" : "exist"} — they now point to the nearest page, please check them`
    );
  }
  if (c.navigator_drafted) {
    lines.push("The Navigator was moved to draft — review and approve each page");
  } else if (c.heavily_edited_question_count > 0) {
    const singular = c.heavily_edited_question_count === 1;
    lines.push(
      `${c.heavily_edited_question_count} Navigator question${singular ? "" : "s"} ` +
        `${singular ? "was" : "were"} moved to draft — ${singular ? "its" : "their"} page's content changed significantly, review and approve before ${singular ? "it goes" : "they go"} live again`
    );
  }
  if (c.orphaned_question_count > 0) {
    lines.push(
      `${c.orphaned_question_count} Navigator question${c.orphaned_question_count === 1 ? "" : "s"} ` +
        `${c.orphaned_question_count === 1 ? "was" : "were"} on a page that no longer exists in this PDF — ` +
        `moved to draft for you to review, reassign, or delete`
    );
  }
  if (c.unplaced_question_count > 0) {
    lines.push(
      `${c.unplaced_question_count} Navigator question${c.unplaced_question_count === 1 ? "" : "s"} ` +
        `couldn't be placed anywhere — please move ${c.unplaced_question_count === 1 ? "it" : "them"} manually`
    );
  }
  return lines;
};

const overallBanner = (c) => {
  if (!c || c.status !== "done") return null;
  if (c.overall === "entirely_new") {
    return "This looks like an entirely different document — no pages matched the previous PDF.";
  }
  if (typeof c.percent_new === "number" && c.percent_new > 0.6) {
    return "Most of this PDF is new content.";
  }
  return null;
};

const formatReplacedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const buildChangedLines = (r) => {
  const lines = [];

  if (r.page_count && r.page_count.from != null && r.page_count.to != null) {
    lines.push(`Page count went from ${r.page_count.from} to ${r.page_count.to}`);
  }

  if (r.orphaned_elements > 0) {
    lines.push(
      `${r.orphaned_elements} item${r.orphaned_elements === 1 ? "" : "s"} ` +
        `(overlays / questions) ${r.orphaned_elements === 1 ? "is" : "are"} on ` +
        `pages the new PDF no longer has and won't be shown`
    );
  }

  const t = r.kdf_tags;
  if (t) {
    if (t.cleared && t.to === 0 && t.from > 0) {
      lines.push(`The ${t.from} KDF tag${t.from === 1 ? "" : "s"} were removed`);
    } else if (t.cleared && t.from > 0) {
      lines.push(`KDF tags were replaced to match the new PDF (${t.from} → ${t.to})`);
    } else if (t.to > t.from) {
      lines.push(
        `${t.to - t.from} KDF tag${t.to - t.from === 1 ? "" : "s"} from the new PDF ` +
          `${t.to - t.from === 1 ? "was" : "were"} added (KDF now has ${t.to})`
      );
    }
  }

  if (r.page_tags_reset) {
    lines.push("Page tags were cleared and re-generated from the new PDF");
  }

  return lines;
};

const styles = {
  intro: { margin: "0 0 14px", lineHeight: 1.5, color: "#3a3a3e" },
  fileCard: {
    background: "#f6f6f7",
    border: "1px solid #e6e6e8",
    borderRadius: "10px",
    padding: "14px 16px",
  },
  fileRow: { display: "flex", alignItems: "center", gap: "10px", minWidth: 0 },
  fileName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "13.5px",
  },
  oldName: { color: "#8a8a8f", textDecoration: "line-through" },
  newName: { color: "#1f1f22", fontWeight: 600 },
  arrow: { color: "#b5b5ba", margin: "6px 0 6px 3px" },
  meta: { fontSize: "12.5px", color: "#8a8a8f", marginTop: "10px" },
  section: { marginTop: "18px" },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#9a9aa0",
    margin: "0 0 7px",
  },
  list: {
    margin: 0,
    paddingLeft: "18px",
    fontSize: "13.5px",
    lineHeight: 1.6,
    color: "#3a3a3e",
  },
  divider: { border: 0, borderTop: "1px solid #ececee", margin: "16px 0 0" },
  banner: {
    background: "#fdf0e8",
    border: "1px solid #f2c9ad",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    color: "#8a4a24",
    margin: "0 0 14px",
    fontWeight: 600,
  },
  infoBanner: {
    background: "#eaf1fb",
    border: "1px solid #bfd6f2",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    color: "#1f4e8c",
    margin: "14px 0 0",
    lineHeight: 1.5,
  },
  revertBtn: {
    background: "none",
    border: 0,
    color: "#294561",
    fontSize: "12.5px",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
  },
};

const GuideQuestion = ({ docId, guide }) => {
  const fileInputRef = React.useRef(null);
  const [status, setStatus] = React.useState(null);
  const [error, setError] = React.useState(null);

  const upload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setError(null);
      setStatus("uploading");
      try {
        await networkService(`docs/${docId}/files/${guide.docs_file_id}/ai_context.json`, "PATCH", {
          content: reader.result,
          filename: file.name,
        });
        setStatus("uploaded");
      } catch (e) {
        setError(e?.response?.data?.error || "Failed to upload the implementation guide. Try again from the Navigator panel.");
        setStatus(null);
      }
    };
    reader.readAsText(file);
  };

  if (status === "uploaded") {
    return <div style={styles.infoBanner}>The new implementation guide was uploaded and is being applied. You can follow it in the Navigator panel.</div>;
  }
  if (status === "kept") {
    return <div style={styles.infoBanner}>Keeping the current implementation guide "{guide.filename}".</div>;
  }

  return (
    <div style={styles.infoBanner}>
      <p style={{ margin: "0 0 10px" }}>
        The PDF was replaced, the current implementation guide "{guide.filename}" has been reactivated.
        {guide.pages_changed && " The pages have changed, so we recommend uploading a newer version."}
        {" "}Would you like to upload a newer version of the implementation guide now?
      </p>
      <input ref={fileInputRef} type="file" accept=".md,.txt" style={{ display: "none" }} onChange={upload} />
      <Button
        text={status === "uploading" ? "Uploading…" : "Upload newer version"}
        classList="success"
        addonClass="my-1"
        disabled={status === "uploading"}
        onClick={() => fileInputRef.current?.click()}
      />{" "}
      <button type="button" style={styles.revertBtn} disabled={status === "uploading"} onClick={() => setStatus("kept")}>
        Keep current guide
      </button>
      {error && <div style={{ color: "#b3261e", marginTop: "8px" }} role="alert">{error}</div>}
    </div>
  );
};

const PdfReplacedModal = ({ docId, replacement, onDismiss }) => {
  const [confirmingRevert, setConfirmingRevert] = React.useState(false);
  const [reverting, setReverting] = React.useState(false);

  if (!replacement) return null;

  const handleDismiss = async () => {
    try {
      await networkService(`docs/${docId}/file-replacements/acknowledge.json`, "POST", {});
    } catch (error) {
      console.error("Failed to acknowledge PDF replacement:", error);
    }
    onDismiss();
  };

  const handleRevert = async () => {
    setReverting(true);
    try {
      await networkService(
        `docs/${docId}/file-replacements/${replacement.id}/revert.json`,
        "POST",
        {}
      );
      window.location.reload();
    } catch (error) {
      console.error("Failed to revert PDF replacement:", error);
      setReverting(false);
      setConfirmingRevert(false);
    }
  };

  const when = formatReplacedAt(replacement.replaced_at);
  const changed = [...buildChangedLines(replacement), ...buildComparisonLines(replacement.comparison)];
  const banner = overallBanner(replacement.comparison);

  return (
    <CommonModal
      title="This document was updated"
      modalStatus={true}
      changeActiveStatus={() => {}}
      centered
      classList="pdf-replaced-modal"
      closeModalOnEsc={false}
      hideCloseIcon
      footer={
        <Button
          text="Got it"
          classList="success"
          addonClass="my-1 common-modal-yes-button"
          onClick={handleDismiss}
        />
      }
    >
      <div style={{ fontSize: "14px", color: "#3a3a3e" }}>
        <p style={styles.intro}>
          The PDF behind this KDF was replaced. Here's what changed so nothing catches you
          off guard.
        </p>

        {banner && <div style={styles.banner}>{banner}</div>}

        <div style={styles.fileCard}>
          <div style={styles.fileRow}>
            <FontAwesomeIcon icon={faFilePdf} style={{ color: "#c1c1c6" }} />
            <span style={{ ...styles.fileName, ...styles.oldName }}>
              {replacement.old_title || "Previous PDF"}
            </span>
          </div>
          <FontAwesomeIcon icon={faArrowDownLong} style={styles.arrow} />
          <div style={styles.fileRow}>
            <FontAwesomeIcon icon={faFilePdf} style={{ color: "#e0524f" }} />
            <span style={{ ...styles.fileName, ...styles.newName }}>
              {replacement.new_title || "New PDF"}
            </span>
          </div>
        </div>

        {(replacement.replaced_by || when) && (
          <div style={styles.meta}>
            {replacement.replaced_by ? `Replaced by ${replacement.replaced_by}` : "Replaced"}
            {when ? ` · ${when}` : ""}
          </div>
        )}

        {replacement.was_published && (
          <div style={styles.infoBanner}>
            The old version is still published. When you're ready, click Republish and this new
            version goes live, replacing the old one. No need to update your LaserLINX — they
            automatically display the new version after you republish.
          </div>
        )}

        {replacement.guide && <GuideQuestion docId={docId} guide={replacement.guide} />}

        <hr style={styles.divider} />

        <div style={styles.section}>
          <div style={styles.sectionLabel}>What changed</div>
          <ul style={styles.list}>
            {changed.length > 0 ? (
              changed.map((line, i) => <li key={i}>{line}</li>)
            ) : (
              <li>The pages were swapped for the new PDF</li>
            )}
          </ul>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionLabel}>What stayed the same</div>
          <ul style={styles.list}>
            {keptItems(replacement).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>

        {replacement.revertable && (
          <>
            <hr style={styles.divider} />
            <div style={{ ...styles.meta, marginTop: "12px" }}>
              {confirmingRevert ? (
                <span>
                  Put back the previous PDF and undo these moves?{" "}
                  <button
                    type="button"
                    style={styles.revertBtn}
                    onClick={handleRevert}
                    disabled={reverting}
                  >
                    {reverting ? "Reverting…" : "Yes, revert"}
                  </button>{" "}
                  <button
                    type="button"
                    style={styles.revertBtn}
                    onClick={() => setConfirmingRevert(false)}
                    disabled={reverting}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  style={styles.revertBtn}
                  onClick={() => setConfirmingRevert(true)}
                >
                  Revert to {replacement.old_title || "the previous PDF"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default PdfReplacedModal;
